/**
 * Hue Background Service
 * Coordinates all Hue services and manages lifecycle
 */

import { Platform } from '@/lib/platform'
import { hueLogger, leaderLogger } from '@/lib/logger'
import { hueBridgeService } from './HueBridgeService'
import { hueSceneConfigSyncService } from './HueSceneConfigSyncService'
import { hueSyncCoordinator } from './HueSyncCoordinator'
import { lightPresenceService, type PresencePayload, type LightState } from '../realtime/LightPresenceService'
import { useHueBridgesStore } from '@/stores/hueBridges'
import { useLightsStore } from '@/stores/lights'
import { useScenesStore } from '@/stores/scenes'
import { useSceneLightConfigsStore } from '@/stores/sceneLightConfigs'
import { getDataProvider, TABLES } from '@/lib/dataProvider'

export interface HueServiceStatus {
  isRunning: boolean
  activeBridges: number
  syncStatus: {
    isRunning: boolean
    activeBridges: string[]
    subscriptions: string[]
  }
  lastDiscovery: number | null
  platform: string
  capabilities: {
    localDiscovery: boolean
    backgroundSync: boolean
    persistentStorage: boolean
  }
}

export interface ServiceEvent {
  type: 'bridge_connected' | 'bridge_disconnected' | 'sync_started' | 'sync_stopped' | 'error'
  bridgeId?: string
  message: string
  timestamp: number
}

export class HueBackgroundService {
  private isRunning = false
  private bridgesStore: ReturnType<typeof useHueBridgesStore> | null = null
  private lightsStore: ReturnType<typeof useLightsStore> | null = null
  private scenesStore: ReturnType<typeof useScenesStore> | null = null
  private sceneLightConfigsStore: ReturnType<typeof useSceneLightConfigsStore> | null = null
  private periodicDiscoveryInterval: NodeJS.Timeout | null = null
  private connectivityCheckInterval: NodeJS.Timeout | null = null
  private eventListeners = new Set<(event: ServiceEvent) => void>()
  
  // Leader-only intervals for bridge writes and DB persistence
  private bridgeWriteInterval: NodeJS.Timeout | null = null
  private dbPersistenceTimeout: NodeJS.Timeout | null = null
  
  // Track light state changes from presence broadcasts
  private changedLights = new Map<string, {
    state: any
    lastBridgeWrite: number
    lastDbWrite: number
  }>()
  
  // DB persistence queue (debounced writes)
  private dbPersistenceQueue = new Map<string, any>()
  private lastDbActivityTime: number = 0

  private static readonly DISCOVERY_INTERVAL = 5 * 60 * 1000 // 5 minutes
  private static readonly CONNECTIVITY_CHECK_INTERVAL = 60 * 1000 // 1 minute
  private static readonly BRIDGE_WRITE_INTERVAL = 1500 // 1.5 seconds
  private static readonly DB_PERSISTENCE_DEBOUNCE = 5000 // 5 seconds

  constructor() {
    // Initialize stores if in Vue context
    try {
      this.bridgesStore = useHueBridgesStore()
      this.lightsStore = useLightsStore()
      this.scenesStore = useScenesStore()
      this.sceneLightConfigsStore = useSceneLightConfigsStore()
    } catch {
      // Will be initialized later when called from Vue context
    }
  }

  /**
   * Initialize the background service
   */
  async initialize(): Promise<void> {
    // Skip Hue service in web browser - can't make HTTP requests to local network devices
    // due to mixed content (HTTPS->HTTP) and CORS restrictions
    if (Platform.isWeb()) {
      hueLogger.info('Skipping Hue service - not available in web browser')
      return
    }

    if (this.isRunning) {
      hueLogger.warn('Hue background service already running')
      return
    }

    try {
      this.isRunning = true
      this.emitEvent({
        type: 'sync_started',
        message: 'Hue background service starting',
        timestamp: Date.now()
      })

      hueLogger.info('Starting Hue background service on', Platform.getPlatformName())

      // Initialize stores if not already done
      if (!this.bridgesStore) {
        this.bridgesStore = useHueBridgesStore()
      }
      if (!this.lightsStore) {
        this.lightsStore = useLightsStore()
      }
      if (!this.scenesStore) {
        this.scenesStore = useScenesStore()
      }

      // Load existing bridges and lights
      await this.loadExistingData()

      // Subscribe to presence state changes for tracking
      hueLogger.debug('📡 Subscribing to presence state changes...')
      lightPresenceService.onStateChange(this.handlePresenceStateChange.bind(this))
      hueLogger.success('✅ Presence subscription active')

      // Start sync coordinator for manual leader selection
      hueLogger.debug('🎯 Starting sync coordinator...')
      const primaryBridgeId = await this.getPrimaryBridgeId()
      if (primaryBridgeId) {
        const { getDeviceName } = await import('@/lib/deviceInfo')
        const deviceName = getDeviceName()
        await hueSyncCoordinator.start(primaryBridgeId, deviceName)
        hueLogger.success('✅ Sync coordinator started')
      } else {
        hueLogger.warn('⚠️ No active bridge found - sync coordinator not started')
      }

      // Start scene config sync service
      hueLogger.debug('🚀 Starting scene config sync service...')
      await hueSceneConfigSyncService.start()
      hueLogger.success('✅ Scene config sync service started')

      // Start leader-only intervals (bridge writes + DB persistence)
      hueLogger.debug('👑 Starting leader interval tasks...')
      this.startLeaderIntervals()
      hueLogger.success('✅ Leader intervals started')

      // Start periodic tasks (desktop only)
      if (Platform.isDesktop()) {
        hueLogger.debug('🔄 Starting periodic background tasks...')
        this.startPeriodicTasks()
      }

      const syncStatus = hueSceneConfigSyncService.getSyncStatus()
      hueLogger.info('📊 Scene config sync status:', syncStatus)

      hueLogger.success('✅ Hue background service started successfully')

    } catch (error) {
      hueLogger.error('Failed to initialize Hue background service:', error)
      this.emitEvent({
        type: 'error',
        message: `Initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: Date.now()
      })
      this.isRunning = false
    }
  }

  /**
   * Shutdown the background service
   */
  async shutdown(): Promise<void> {
    if (!this.isRunning) {
      return
    }

    hueLogger.info('Shutting down Hue background service')
    this.isRunning = false

    // Stop leader intervals
    this.stopLeaderIntervals()

    // Stop periodic tasks
    if (this.periodicDiscoveryInterval) {
      clearInterval(this.periodicDiscoveryInterval)
      this.periodicDiscoveryInterval = null
    }

    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval)
      this.connectivityCheckInterval = null
    }

    // Flush any pending DB writes before shutdown
    await this.flushDbWrites()

    // Stop scene config sync service
    await hueSceneConfigSyncService.stop()

    // Stop sync coordinator
    await hueSyncCoordinator.stop()

    // Clear tracking maps
    this.changedLights.clear()
    this.dbPersistenceQueue.clear()

    this.emitEvent({
      type: 'sync_stopped',
      message: 'Hue background service stopped',
      timestamp: Date.now()
    })

    hueLogger.info('Hue background service shut down')
  }

  /**
   * Load existing bridges and lights from database
   */
  private async loadExistingData(): Promise<void> {
    try {
      if (!this.bridgesStore || !this.lightsStore) {
        throw new Error('Stores not initialized')
      }

      // Load bridges
      await this.bridgesStore.fetchBridges()
      
      // Load lights
      await this.lightsStore.fetchLights()

      // Check connectivity for all bridges
      await this.bridgesStore.checkBridgeConnectivity()

      hueLogger.info(`Loaded ${this.bridgesStore.allBridges.length} bridges and ${this.lightsStore.allLights.length} lights`)

      // Sync actual light state from bridges to ensure current_state is fresh
      hueLogger.debug('🔄 Syncing light states from bridges...')
      await this.syncAllLights()
      hueLogger.success('✅ Light states synced from bridges')
    } catch (error) {
      hueLogger.error('Failed to load existing data:', error)
      throw error
    }
  }

  /**
   * Get the primary active bridge ID (first active bridge)
   */
  private async getPrimaryBridgeId(): Promise<string | null> {
    try {
      if (!this.bridgesStore) return null
      
      const allBridges = this.bridgesStore.allBridges
      const activeBridge = allBridges.find(b => b.is_active)
      
      return activeBridge?.bridge_id || null
    } catch (error) {
      hueLogger.error('Failed to get primary bridge ID:', error)
      return null
    }
  }

  /**
   * Start periodic background tasks (desktop only)
   */
  private startPeriodicTasks(): void {
    if (!Platform.isDesktop()) {
      return
    }

    hueLogger.debug('Starting periodic background tasks')

    // Periodic bridge discovery
    this.periodicDiscoveryInterval = setInterval(async () => {
      try {
        if (this.bridgesStore) {
          await this.bridgesStore.discoverBridges()
        }
      } catch (error) {
        hueLogger.error('Periodic discovery failed:', error)
      }
    }, HueBackgroundService.DISCOVERY_INTERVAL)

    // Periodic connectivity check
    this.connectivityCheckInterval = setInterval(async () => {
      try {
        if (this.bridgesStore) {
          await this.bridgesStore.checkBridgeConnectivity()
        }
      } catch (error) {
        hueLogger.error('Connectivity check failed:', error)
      }
    }, HueBackgroundService.CONNECTIVITY_CHECK_INTERVAL)
  }

  /**
   * Add a new bridge
   */
  async addBridge(ipAddress: string): Promise<boolean> {
    try {
      if (!this.bridgesStore) {
        throw new Error('Bridges store not initialized')
      }

      // First discover the bridge
      const discovered = await this.bridgesStore.addBridgeByIP(ipAddress)
      if (!discovered) {
        return false
      }

      // Then connect to it
      const success = await this.bridgesStore.connectToBridge(discovered)
      
      if (success) {
        this.emitEvent({
          type: 'bridge_connected',
          bridgeId: discovered.id,
          message: `Connected to bridge at ${ipAddress}`,
          timestamp: Date.now()
        })
      }

      return success
    } catch (error) {
      hueLogger.error('Failed to add bridge:', error)
      this.emitEvent({
        type: 'error',
        message: `Failed to add bridge: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: Date.now()
      })
      return false
    }
  }

  /**
   * Remove a bridge
   */
  async removeBridge(bridgeId: string): Promise<boolean> {
    try {
      if (!this.bridgesStore) {
        throw new Error('Bridges store not initialized')
      }

      await this.bridgesStore.removeBridge(bridgeId)

      this.emitEvent({
        type: 'bridge_disconnected',
        bridgeId,
        message: `Removed bridge ${bridgeId}`,
        timestamp: Date.now()
      })

      return true
    } catch (error) {
      hueLogger.error('Failed to remove bridge:', error)
      this.emitEvent({
        type: 'error',
        bridgeId,
        message: `Failed to remove bridge: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: Date.now()
      })
      return false
    }
  }

  /**
   * Manually discover bridges
   */
  async discoverBridges(): Promise<void> {
    try {
      if (!this.bridgesStore) {
        throw new Error('Bridges store not initialized')
      }

      await this.bridgesStore.discoverBridges()
    } catch (error) {
      hueLogger.error('Manual bridge discovery failed:', error)
      this.emitEvent({
        type: 'error',
        message: `Discovery failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: Date.now()
      })
    }
  }

  /**
   * Get current service status
   */
  getStatus(): HueServiceStatus {
    const sceneConfigStatus = hueSceneConfigSyncService.getSyncStatus()
    
    return {
      isRunning: this.isRunning,
      activeBridges: this.bridgesStore?.onlineBridges.length ?? 0,
      syncStatus: {
        isRunning: sceneConfigStatus.isRunning,
        activeBridges: sceneConfigStatus.activeBridges,
        subscriptions: sceneConfigStatus.activeScene ? [sceneConfigStatus.activeScene] : []
      },
      lastDiscovery: null, // Could track this if needed
      platform: Platform.getPlatformName(),
      capabilities: {
        localDiscovery: Platform.hasCapability('localDiscovery'),
        backgroundSync: Platform.hasCapability('backgroundSync'),
        persistentStorage: Platform.hasCapability('secureStorage')
      }
    }
  }

  /**
   * Event system for service notifications
   */
  addEventListener(listener: (event: ServiceEvent) => void): void {
    this.eventListeners.add(listener)
  }

  removeEventListener(listener: (event: ServiceEvent) => void): void {
    this.eventListeners.delete(listener)
  }

  private emitEvent(event: ServiceEvent): void {
    for (const listener of this.eventListeners) {
      try {
        listener(event)
      } catch (error) {
        hueLogger.error('Event listener error:', error)
      }
    }
  }

  /**
   * Force sync all lights from all bridges
   */
  async syncAllLights(): Promise<void> {
    try {
      if (!this.bridgesStore) {
        throw new Error('Bridges store not initialized')
      }

      const onlineBridges = this.bridgesStore.onlineBridges
      
      for (const bridge of onlineBridges) {
        await this.bridgesStore.syncAllLightsFromBridge(bridge.bridge_id)
      }

      hueLogger.info(`Synced lights from ${onlineBridges.length} bridges`)
    } catch (error) {
      hueLogger.error('Failed to sync all lights:', error)
      this.emitEvent({
        type: 'error',
        message: `Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: Date.now()
      })
    }
  }

  /**
   * Check if service is running
   */
  get running(): boolean {
    return this.isRunning
  }

  /**
   * Check if background service is managing sync
   */
  get isManagingSync(): boolean {
    return this.isRunning && hueSceneConfigSyncService.getSyncStatus().isRunning
  }

  /**
   * Update active scene (call this when user changes scenes)
   */
  async setActiveScene(sceneId: string | null): Promise<void> {
    await hueSceneConfigSyncService.updateActiveScene(sceneId)
  }

  /**
   * Check if new state is different from existing tracked state
   */
  private hasStateChanged(existingState: LightState | undefined, newState: LightState): boolean {
    if (!existingState) {
      return true // No existing state, this is a new change
    }

    // Check each field that might be present
    if (newState.on !== undefined && newState.on !== existingState.on) return true
    if (newState.brightness !== undefined && newState.brightness !== existingState.brightness) return true
    if (newState.hue !== undefined && newState.hue !== existingState.hue) return true
    if (newState.saturation !== undefined && newState.saturation !== existingState.saturation) return true
    if (newState.ct !== undefined && newState.ct !== existingState.ct) return true
    if (newState.xy !== undefined) {
      if (!existingState.xy) return true
      if (newState.xy[0] !== existingState.xy[0] || newState.xy[1] !== existingState.xy[1]) return true
    }

    return false // No changes detected
  }

  /**
   * Track local state change (called by composables when state changes locally)
   * This is separate from handlePresenceStateChange because we also need to track our own changes
   */
  trackLocalStateChange(lightId: string, state: LightState): void {
    const now = Date.now()
    
    // Check if state actually changed
    const existing = this.changedLights.get(lightId)
    if (!this.hasStateChanged(existing?.state, state)) {
      return // No actual change, skip tracking
    }
    
    // Track this light as changed
    this.changedLights.set(lightId, {
      state,
      lastBridgeWrite: existing?.lastBridgeWrite || 0,
      lastDbWrite: existing?.lastDbWrite || 0
    })
    
    // Queue for DB persistence
    this.queueDbPersistence(lightId, state)
    
    hueLogger.debug(`📝 [HueBackgroundService] Tracked LOCAL change for light ${lightId}`, state)
  }

  /**
   * Handle presence state changes from other clients - track which lights have changed
   */
  private handlePresenceStateChange(payload: PresencePayload): void {
    const now = Date.now()
    
    // Check if state actually changed
    const existing = this.changedLights.get(payload.lightId)
    if (!this.hasStateChanged(existing?.state, payload.state)) {
      return // No actual change, skip tracking
    }
    
    // Track this light as changed
    this.changedLights.set(payload.lightId, {
      state: payload.state,
      lastBridgeWrite: existing?.lastBridgeWrite || 0,
      lastDbWrite: existing?.lastDbWrite || 0
    })
    
    // Queue for DB persistence
    this.queueDbPersistence(payload.lightId, payload.state)
    
    hueLogger.debug(`📝 [HueBackgroundService] Tracked PEER change for light ${payload.lightId}`, payload.state)
  }

  /**
   * Start leader-only intervals for bridge writes and DB persistence
   */
  private startLeaderIntervals(): void {
    // Bridge write loop (1.5 seconds)
    this.bridgeWriteInterval = setInterval(async () => {
      await this.processBridgeWrites()
    }, HueBackgroundService.BRIDGE_WRITE_INTERVAL)
    
    leaderLogger.success('✅ Leader intervals started (bridge: 1.5s, DB: 5s debounce)')
  }

  /**
   * Stop leader intervals
   */
  private stopLeaderIntervals(): void {
    if (this.bridgeWriteInterval) {
      clearInterval(this.bridgeWriteInterval)
      this.bridgeWriteInterval = null
    }
    
    if (this.dbPersistenceTimeout) {
      clearTimeout(this.dbPersistenceTimeout)
      this.dbPersistenceTimeout = null
    }
    
    leaderLogger.info('🛑 Leader intervals stopped')
  }

  /**
   * Process bridge writes (leader only, every 1.5 seconds)
   * Batches lights by bridge and state for efficient zone-based control
   */
  private async processBridgeWrites(): Promise<void> {
    try {
      // Check if we're the leader
      const isLeader = await hueSyncCoordinator.checkIsLeader()
      if (!isLeader) {
        // Only log once when we have pending changes
        if (this.changedLights.size > 0) {
          leaderLogger.info(`⏸️ [Follower] Skipping bridge write for ${this.changedLights.size} lights - not leader`)
        }
        return // Follower - don't write to bridge
      }
      
      const now = Date.now()
      const lightsToWrite: Array<{ lightId: string; state: any }> = []
      
      // Find lights that haven't been written to bridge in the last second
      for (const [lightId, data] of this.changedLights.entries()) {
        if (now - data.lastBridgeWrite >= HueBackgroundService.BRIDGE_WRITE_INTERVAL) {
          lightsToWrite.push({ lightId, state: data.state })
        }
      }
      
      if (lightsToWrite.length === 0) {
        return // No lights to write
      }
      
      leaderLogger.info(`👑 [Leader] Writing ${lightsToWrite.length} lights to bridge`)
      
      // Group lights by bridge for batch processing
      const lightsByBridge = await this.groupLightsByBridge(lightsToWrite)
      
      // Process each bridge's lights
      for (const [bridgeId, bridgeLights] of lightsByBridge.entries()) {
        try {
          await this.writeBridgeLightsBatch(bridgeId, bridgeLights)
        } catch (error) {
          leaderLogger.error(`Failed to write lights to bridge ${bridgeId}:`, error)
        }
      }
      
    } catch (error) {
      leaderLogger.error('Error processing bridge writes:', error)
    }
  }

  /**
   * Group lights by their bridge and normalize states
   */
  private async groupLightsByBridge(lights: Array<{ lightId: string; state: any }>): Promise<Map<string, Array<{ lightId: string; hueId: string; state: LightState }>>> {
    const byBridge = new Map<string, Array<{ lightId: string; hueId: string; state: LightState }>>()
    
    if (!this.lightsStore) {
      return byBridge
    }
    
    for (const { lightId, state } of lights) {
      const light = this.lightsStore.getLight(lightId)
      if (!light?.hue_bridge_id || !light.hue_light_id) {
        hueLogger.warn(`Light ${lightId} missing bridge or hue ID`)
        continue
      }
      
      const normalizedState = this.normalizeStateForBridge(light, state)
      
      if (!byBridge.has(light.hue_bridge_id)) {
        byBridge.set(light.hue_bridge_id, [])
      }
      
      byBridge.get(light.hue_bridge_id)!.push({
        lightId,
        hueId: light.hue_light_id,
        state: normalizedState
      })
    }
    
    return byBridge
  }

  /**
   * Normalize state for bridge communication (convert HS to XY, etc.)
   */
  private normalizeStateForBridge(light: any, state: any): LightState {
    const lightState: any = {}
    
    // Always include on/bri if provided, or get from current state
    if (state.on !== undefined) {
      lightState.on = state.on
    } else if (light.current_state?.on !== undefined) {
      lightState.on = light.current_state.on
    }
    
    if (state.brightness !== undefined) {
      lightState.brightness = state.brightness
    } else if (light.current_state?.bri !== undefined) {
      lightState.brightness = light.current_state.bri
    }
    
    // Color mode: CT takes priority
    if (state.ct !== undefined) {
      lightState.ct = state.ct
    } else if (state.hue !== undefined && state.saturation !== undefined) {
      // Convert hue/saturation to XY for better compatibility
      const xy = this.convertHSToXY(state.hue, state.saturation)
      lightState.xy = xy
    } else if (state.xy && Array.isArray(state.xy)) {
      lightState.xy = state.xy
    }
    
    return lightState
  }

  /**
   * Write all lights for a single bridge individually
   */
  private async writeBridgeLightsBatch(bridgeId: string, lights: Array<{ lightId: string; hueId: string; state: LightState }>): Promise<void> {
    if (!this.bridgesStore) {
      return
    }
    
    const bridge = this.bridgesStore.getBridge(bridgeId)
    if (!bridge?.ip_address) {
      leaderLogger.warn(`Bridge ${bridgeId} not found or has no IP`)
      return
    }
    
    leaderLogger.info(`👑 [Leader] Writing ${lights.length} lights individually to bridge`)
    
    // Write each light individually
    let successCount = 0
    let errorCount = 0
    
    for (const { lightId, hueId, state } of lights) {
      try {
        const light = this.lightsStore?.getLight(lightId)
        leaderLogger.debug(`🔧 [Leader] Writing light ${light?.name || lightId} (${hueId}):`, state)
        
        await hueBridgeService.setLightState(
          bridgeId,
          bridge.ip_address,
          hueId,
          state,
          4 // 400ms transition
        )
        
        // Remove from tracking on success
        this.changedLights.delete(lightId)
        successCount++
      } catch (error) {
        leaderLogger.error(`Failed to write light ${lightId}:`, error)
        errorCount++
      }
    }
    
    if (successCount > 0) {
      leaderLogger.success(`✅ [Leader] Wrote ${successCount} lights to bridge`)
    }
    if (errorCount > 0) {
      leaderLogger.warn(`⚠️ [Leader] Failed to write ${errorCount} lights`)
    }
  }

  /**
   * Convert Hue/Saturation values to XY coordinates
   * Based on Philips Hue color conversion algorithm
   */
  private convertHSToXY(hue: number, saturation: number): [number, number] {
    // Normalize hue (0-65535) to 0-360 degrees
    const hueDegrees = (hue / 65535) * 360
    
    // Normalize saturation (0-254) to 0-1
    const satNormalized = saturation / 254
    
    // Convert to radians
    const hueRadians = (hueDegrees * Math.PI) / 180
    
    // Calculate XY using standard color space conversion
    // This uses a simplified conversion that works well for most Hue lights
    const x = (1 + satNormalized * Math.cos(hueRadians)) / 3
    const y = (1 + satNormalized * Math.sin(hueRadians)) / 3
    
    // Clamp to valid XY range (0-1)
    const clampedX = Math.max(0, Math.min(1, x))
    const clampedY = Math.max(0, Math.min(1, y))
    
    // Round to 4 decimal places for precision
    return [
      Math.round(clampedX * 10000) / 10000,
      Math.round(clampedY * 10000) / 10000
    ]
  }

  /**
   * Queue light state for DB persistence (debounced)
   */
  private queueDbPersistence(lightId: string, state: any): void {
    // Add to queue
    this.dbPersistenceQueue.set(lightId, state)
    this.lastDbActivityTime = Date.now()
    
    // Clear existing timeout
    if (this.dbPersistenceTimeout) {
      clearTimeout(this.dbPersistenceTimeout)
    }
    
    // Set new timeout - flush after 5 seconds of inactivity
    this.dbPersistenceTimeout = setTimeout(async () => {
      await this.flushDbWrites()
    }, HueBackgroundService.DB_PERSISTENCE_DEBOUNCE)
  }

  /**
   * Flush queued DB writes (leader only)
   */
  private async flushDbWrites(): Promise<void> {
    if (this.dbPersistenceQueue.size === 0) {
      return
    }
    
    try {
      // Check if we're the leader
      const isLeader = await hueSyncCoordinator.checkIsLeader()
      if (!isLeader) {
        hueLogger.debug('⏭️ Skipping DB writes - not the leader')
        this.dbPersistenceQueue.clear()
        return
      }
      
      // Get active scene
      const activeScene = this.scenesStore?.getActiveScene
      if (!activeScene) {
        leaderLogger.warn('No active scene - skipping DB writes')
        this.dbPersistenceQueue.clear()
        return
      }
      
      leaderLogger.info(`👑 [Leader] Flushing ${this.dbPersistenceQueue.size} lights to DB`)
      
      const now = Date.now()
      const updates: Array<{ lightId: string; state: any }> = []
      
      // Collect lights to update
      for (const [lightId, state] of this.dbPersistenceQueue.entries()) {
        const data = this.changedLights.get(lightId)
        
        // Only write if enough time has passed since last DB write
        if (!data || now - data.lastDbWrite >= HueBackgroundService.DB_PERSISTENCE_DEBOUNCE) {
          updates.push({ lightId, state })
        }
      }
      
      if (updates.length === 0) {
        this.dbPersistenceQueue.clear()
        return
      }
      
      // Batch update scene_light_configs
      for (const { lightId, state } of updates) {
        try {
          await this.updateSceneLightConfig(activeScene.id, lightId, state)
          
          // Update last DB write time
          const data = this.changedLights.get(lightId)
          if (data) {
            data.lastDbWrite = now
          }
        } catch (error) {
          leaderLogger.error(`Failed to update scene config for light ${lightId}:`, error)
        }
      }
      
      leaderLogger.success(`✅ [Leader] Wrote ${updates.length} lights to DB`)
      
      // Clear queue
      this.dbPersistenceQueue.clear()
      
    } catch (error) {
      leaderLogger.error('Error flushing DB writes:', error)
    }
  }

  /**
   * Update scene_light_configs for a specific light
   * Also updates the local store to keep inheritance state in sync
   */
  private async updateSceneLightConfig(sceneId: string, lightId: string, state: any): Promise<void> {
    // Build target_state object
    const targetState: any = {
      on: state.on,
      brightness: state.brightness
    }
    
    // Color mode
    if (state.ct !== undefined) {
      targetState.color_temperature = state.ct
    } else if (state.hue !== undefined && state.saturation !== undefined) {
      targetState.hue = state.hue
      targetState.saturation = state.saturation
    } else if (state.xy && Array.isArray(state.xy)) {
      targetState.xy = state.xy
    }
    
    // Upsert scene_light_configs
    const provider = getDataProvider()
    const result = await provider.upsert(TABLES.SCENE_LIGHT_CONFIGS, {
      scene_id: sceneId,
      light_id: lightId,
      target_state: targetState
    }, 'scene_id,light_id')
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to update scene light config')
    }
    
    // Refresh the configs from the store to get the full updated data
    if (this.sceneLightConfigsStore) {
      await this.sceneLightConfigsStore.fetchConfigs()
      leaderLogger.debug(`📝 Refreshed configs after updating light ${lightId} in scene ${sceneId}`)
    }
  }
}

// Singleton instance
export const hueBackgroundService = new HueBackgroundService()

// Removed auto-initialization - now handled explicitly in main.ts